<template>
  <div
    :class="[bem.b(), bordered ? bem.is('border') : '']"
    :style="containerStyle"
  >
    <div ref="qrContainer" :style="qrContainerStyle" />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'

import { createNamespace } from '@ll-plus/utils'

import QRCodeStyling, { type Options } from 'qr-code-styling'

import { qrcodeProps } from './qrcode'

defineOptions({ name: 'LlQrcode' })

const bem = createNamespace('qrcode')

const props = defineProps(qrcodeProps)

const qrContainer = ref<HTMLElement | null>(null)
const qrCodeInstance = ref<QRCodeStyling | null>(null)

// 计算容器样式（包括扩展样式）
const containerStyle = computed(() => ({
  padding: `${props.padding}px`,
  width: `${props.size}px`,
  height: `${props.size}px`,
}))

// 二维码容器样式
const qrContainerStyle = computed(() => ({
  width: `${props.size - props.padding * 2}px`,
  height: `${props.size - props.padding * 2}px`,
  background: props.bgColor || '#ffffff',
}))

// 计算二维码尺寸
const qrSize = computed(() => {
  // 确保计算出的尺寸不小于最小值
  const MIN_SIZE = 100

  // 计算基础尺寸
  let calculatedSize = props.size - props.padding * 2

  // 确保计算出的尺寸有效
  if (calculatedSize <= 0) {
    calculatedSize = MIN_SIZE
  }

  // 处理用户自定义尺寸
  const width = Math.max(MIN_SIZE, calculatedSize)
  const height = Math.max(MIN_SIZE, calculatedSize)

  return { width, height }
})

// 构建QR Code配置对象
const buildQrConfig = computed(
  (): Partial<Options> => ({
    // 二维码尺寸
    width: qrSize.value.width,
    height: qrSize.value.height,

    // 核心数据
    data: props.data,
    margin: props.margin,

    // 图像设置
    image: props.image || undefined,
    imageOptions: {
      hideBackgroundDots: props.imageHideBgDots,
      imageSize: props.imageSize,
      margin: props.imageMargin,
      // base64 图片（data: 协议）不能通过 XHR 转 Blob，必须关闭 saveAsBlob，
      // 否则 qr-code-styling 内部会发起同步 XHR 并抛出 InvalidAccessError
      saveAsBlob: !props.image?.startsWith('data:'),
    },

    // 二维码选项
    qrOptions: {
      typeNumber: 0,
      mode: 'Byte',
      errorCorrectionLevel: props.errorLevel,
    },

    // 点样式配置
    dotsOptions: {
      type: props.dotsType,
      color: props.dotsColor || '#000000',
      gradient:
        props.dotsGradientType !== 'single'
          ? {
              type: props.dotsGradientType,
              rotation: props.dotsGradientRotation,
              colorStops: [
                { offset: 0, color: props.dotsGradientStartColor || '#ff0000' },
                { offset: 1, color: props.dotsGradientEndColor || '#0000ff' },
              ],
            }
          : undefined,
    },

    // 边角方块配置
    cornersSquareOptions: {
      type: props.cornersSquareType,
      color: props.cornersSquareColor || '#000000',

      gradient:
        props.cornersSquareGradientType !== 'single'
          ? {
              type: props.cornersSquareGradientType,
              rotation: props.cornersSquareGradientRotation,
              colorStops: [
                {
                  offset: 0,
                  color: props.cornersSquareGradientStartColor || '#ff0000',
                },
                {
                  offset: 1,
                  color: props.cornersSquareGradientEndColor || '#0000ff',
                },
              ],
            }
          : undefined,
    },

    // 边角点配置
    cornersDotOptions: {
      type: props.cornersDotType,
      color: props.cornersDotColor || '#000000',
      gradient:
        props.cornersDotGradientType !== 'single'
          ? {
              type: props.cornersDotGradientType,
              rotation: props.cornersDotGradientRotation,
              colorStops: [
                {
                  offset: 0,
                  color: props.cornersDotGradientStartColor || '#ff0000',
                },
                {
                  offset: 1,
                  color: props.cornersDotGradientEndColor || '#0000ff',
                },
              ],
            }
          : undefined,
    },

    // 背景配置
    backgroundOptions: {
      color: props.bgColor || '#ffffff',
      gradient:
        props.bgGradientType !== 'single'
          ? {
              type: props.bgGradientType,
              rotation: props.bgGradientRotation,
              colorStops: [
                { offset: 0, color: props.bgGradientStartColor || '#ff0000' },
                { offset: 1, color: props.bgGradientEndColor || '#0000ff' },
              ],
            }
          : undefined,
    },
  }),
)

// 初始化QR Code实例
const initQrCode = () => {
  if (!qrContainer.value) return

  // 创建新的QR Code实例
  qrCodeInstance.value = new QRCodeStyling(buildQrConfig.value as Options)

  // 清除容器中已有的内容
  while (qrContainer.value.firstChild) {
    qrContainer.value.removeChild(qrContainer.value.firstChild)
  }

  // 将二维码附加到容器
  qrCodeInstance.value.append(qrContainer.value)
}

// 更新QR Code
const updateQrCode = () => {
  if (qrCodeInstance.value) {
    // 使用新配置更新现有实例
    qrCodeInstance.value.update(buildQrConfig.value as Options)
  } else {
    // 如果实例不存在，则初始化
    initQrCode()
  }
}

// 监听所有props变化（深度监听）
watch(
  () => Object.values(props),
  () => {
    // 在下一个DOM更新周期更新二维码
    nextTick(updateQrCode)
  },
  { deep: true },
)

// 组件挂载后初始化二维码
onMounted(() => {
  nextTick(initQrCode)
})
</script>
